import type { BrokerageAccount } from '../brokerage/brokerage.types.ts';

export interface AccountDuplicateGroup {
  fingerprint: string;
  keptAccountId: string;
  duplicateAccountIds: string[];
  label: string;
  mask: string;
}

export interface AccountDedupeResult {
  accounts: BrokerageAccount[];
  duplicateGroups: AccountDuplicateGroup[];
}

export function dedupeBrokerageAccountsForAnalysis(
  accounts: BrokerageAccount[],
  excludedAccountIds: Set<string> = new Set()
): AccountDedupeResult {
  const groups = new Map<string, BrokerageAccount[]>();

  for (const account of accounts) {
    const fingerprint = accountFingerprint(account);
    groups.set(fingerprint, [...(groups.get(fingerprint) || []), account]);
  }

  const deduped: BrokerageAccount[] = [];
  const duplicateGroups: AccountDuplicateGroup[] = [];

  for (const [fingerprint, group] of groups.entries()) {
    const kept = chooseAccountRepresentative(group, excludedAccountIds);
    deduped.push(kept);

    if (group.length > 1) {
      duplicateGroups.push({
        fingerprint,
        keptAccountId: kept.id,
        duplicateAccountIds: group.filter(account => account.id !== kept.id).map(account => account.id),
        label: accountLabel(kept),
        mask: normalizeMask(kept.number),
      });
    }
  }

  return { accounts: deduped, duplicateGroups };
}

function chooseAccountRepresentative(
  accounts: BrokerageAccount[],
  excludedAccountIds: Set<string>
): BrokerageAccount {
  return [...accounts].sort((a, b) => {
    const selectedDiff = Number(!excludedAccountIds.has(b.id)) - Number(!excludedAccountIds.has(a.id));
    if (selectedDiff !== 0) return selectedDiff;

    const balanceDiff = Math.abs(finiteNumber(b.balance)) - Math.abs(finiteNumber(a.balance));
    if (Math.abs(balanceDiff) > 0.01) return balanceDiff;

    return 0;
  })[0];
}

function accountFingerprint(account: BrokerageAccount): string {
  return [
    normalizeText(account.institutionName),
    normalizeText(account.name),
    normalizeMask(account.number),
    normalizeText(account.currency || ''),
  ].join('|');
}

function accountLabel(account: BrokerageAccount): string {
  return `${account.institutionName} - ${account.name}`.trim();
}

function normalizeText(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function normalizeMask(value: string): string {
  return value.replace(/\D+/g, '').slice(-4);
}

function finiteNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
